import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { useLiveStatus } from '../contexts/LiveStatusContext';
import { isCommunityFeatureEnabled, hasAnyCommunityFeature, getEnabledEmbeddedApps } from '../utils/featureHelpers';
import { EMBEDDED_APP_PATHS } from '../config/embeddedApps';
import '../styles/bottomNavigation.css';


/**
 * Bottom navigation component for mobile video features
 * Shows Home | Shorts | Videos when video options are enabled
 */
const BottomNavigation = () => {
  console.log('[BottomNavigation] Component function called');
  
  const { config, loading, theme } = useConfig();
  const { hasLiveVideos } = useLiveStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const [showLecturesMenu, setShowLecturesMenu] = useState(false);
  const [showVideosMenu, setShowVideosMenu] = useState(false);
  const [showCommunityMenu, setShowCommunityMenu] = useState(false);
  const [showServicesMenu, setShowServicesMenu] = useState(false);
  const menuRef = useRef(null);
  const videosMenuRef = useRef(null);
  const communityMenuRef = useRef(null);
  const servicesMenuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowLecturesMenu(false);
      }
      if (videosMenuRef.current && !videosMenuRef.current.contains(event.target)) {
        setShowVideosMenu(false);
      }
      if (communityMenuRef.current && !communityMenuRef.current.contains(event.target)) {
        setShowCommunityMenu(false);
      }
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(event.target)) {
        setShowServicesMenu(false);
      }
    };

    if (showLecturesMenu || showVideosMenu || showCommunityMenu || showServicesMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showLecturesMenu, showVideosMenu, showCommunityMenu]);

  // Check if we're on a video-related route
  const isVideoRoute = location.pathname === '/shorts' ||
                      location.pathname === '/videos' ||
                      location.pathname === '/lecture-clips' ||
                      location.pathname.startsWith('/videos/');
  
  // Debug logging for navigation visibility
  console.log('[BottomNavigation] Visibility check:', {
    loading,
    video_options_enabled: config?.video_options_enabled,
    isVideoRoute,
    pathname: location.pathname
  });
  
  // Check if any community features are enabled using centralized helper
  const hasCommunityFeatures = hasAnyCommunityFeature(config);
  
  // Show navigation only if videos OR community features are enabled
  // (lectures alone don't show bottom nav)
  const shouldShow = loading || config?.video_options_enabled || hasCommunityFeatures;
  
  if (!shouldShow) {
    console.log('[BottomNavigation] Hiding - only lectures enabled');
    return null;
  }

  // Determine which community features are enabled using centralized helper
  const enabledCommunityFeatures = [];
  
  // Debug logging for community features
  console.log('[BottomNavigation] Community config:', {
    community_portal_enabled: config?.community_portal_enabled,
    announcements: config?.community_features?.announcements,
    calendar: config?.community_features?.calendar,
    qa: config?.community_features?.qa,
    services: config?.community_features?.services,
    community_services_enabled_top_level: config?.community_services_enabled
  });
  
  // Use helper functions for consistent feature checking
  // Services will be added as a separate nav item, not in community features
  if (isCommunityFeatureEnabled(config, 'announcements')) {
    enabledCommunityFeatures.push({ id: 'announcements', label: 'Announcements', path: '/community/announcements' });
  }
  if (isCommunityFeatureEnabled(config, 'calendar')) {
    enabledCommunityFeatures.push({ id: 'calendar', label: 'Calendar', path: '/community/calendar' });
  }
  if (isCommunityFeatureEnabled(config, 'qa')) {
    enabledCommunityFeatures.push({ id: 'qa', label: 'Q&A', path: '/community/qa' });
  }
  // Note: Services removed from here, will be added as standalone item
  
  console.log('[BottomNavigation] Enabled community features:', enabledCommunityFeatures);

  // Navigation items with modern SVG icons
  const navItems = [];
  
  // Add Home item if we have videos OR community features
  // (which means RichHomePage is showing)
  const showRichHomePage = config?.video_options_enabled || hasAnyCommunityFeature(config);
  
  if (showRichHomePage) {
    navItems.push({
      path: '/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      label: 'Home',
      exact: true
    });
  }
  
  // Add Lectures item
  navItems.push({
    path: '/lectures',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
        <line x1="12" y1="6" x2="16" y2="6" />
        <line x1="12" y1="10" x2="16" y2="10" />
      </svg>
    ),
    label: 'Lectures',
    exact: true
  });

  // Add Videos nav item if video options are enabled
  console.log('[BottomNavigation] Video check - video_options_enabled:', config?.video_options_enabled);
  if (config?.video_options_enabled) {
    console.log('[BottomNavigation] Adding Videos to navItems');
    navItems.push({
      path: '/videos',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffff">
          <path d="M10 14.65v-5.3L15 12l-5 2.65z" />
          <path d="M18 4H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H6V6h12v12z" />
          <rect x="7" y="2" width="10" height="2" rx="1" />
          <rect x="7" y="20" width="10" height="2" rx="1" />
        </svg>
      ),
      label: 'Videos',
      exact: true
    });
  }

  // Add Community nav item if any community features are enabled (excluding services)
  if (enabledCommunityFeatures.length === 1) {
    // Single feature: add it directly
    navItems.push({
      path: enabledCommunityFeatures[0].path,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          <line x1="9" y1="10" x2="15" y2="10" />
          <line x1="12" y1="7" x2="12" y2="13" />
        </svg>
      ),
      label: enabledCommunityFeatures[0].label,
      exact: false,
      isCommunity: true,
      communityFeatures: enabledCommunityFeatures
    });
  } else if (enabledCommunityFeatures.length > 1) {
    // Multiple features: add "Community" with submenu
    navItems.push({
      path: '/community',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      ),
      label: 'Community',
      exact: false,
      isCommunity: true,
      communityFeatures: enabledCommunityFeatures
    });
  }
  
  // Add Services as a dedicated nav item if enabled
  const hasTaxDocs = isCommunityFeatureEnabled(config, 'tax_docs');
  // Embedded external apps (Library, Housing, ...) that this center has on.
  // Registry-driven, so a new app appears here with no change to this file.
  const embeddedApps = getEnabledEmbeddedApps(config);
  if (isCommunityFeatureEnabled(config, 'services')) {
    navItems.push({
      path: '/community/services',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          {/* Heart in hands icon - community care and service */}
          <path d="M11 18c4.5-1.5 7-5.5 7-9V5l-6-2-6 2v4c0 3.5 2.5 7.5 7 9" />
          <path d="M9 11.24A3 3 0 0112 9c1.18 0 2.25.68 2.76 1.74a3 3 0 01-.01 3.52l-2.75 3.5-2.75-3.5A3 3 0 019 11.24z" />
        </svg>
      ),
      label: 'Services',
      exact: false,
      isServices: true,
      hasSubMenu: hasTaxDocs || embeddedApps.length > 0
    });
  } else if (hasTaxDocs) {
    // Tax Docs enabled but Services is not — show Tax Docs as a standalone nav item
    navItems.push({
      path: '/community/tax-docs',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <path d="M14 2v6h6" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      ),
      label: 'Tax Docs',
      exact: false
    });
  }

  // Check if a nav item is active
  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    } else {
      return location.pathname.startsWith(item.path);
    }
  };

  // Get theme color
  const themeColor = config?.primaryColor || theme?.colors?.primary || '#001f3f';
  
  // Always use theme color for consistent branding
  const navigationStyle = {
    backgroundImage: `linear-gradient(to bottom, ${themeColor}CC, ${themeColor}F0)`,
    borderTopColor: `${themeColor}40`,
    '--theme-color': themeColor
  };

  // Handle navigation item click
  const handleNavClick = (event, item) => {
    if (item.path === '/' && item.label === 'Home') {
      console.log('[BottomNavigation] Home button clicked');
      // Always navigate to home (this will close any modals and ensure clean state)
      window.location.href = '/';
      return; // Exit early for home button
    } else if (item.path === '/lectures') {
      // If clicking Lectures, show submenu
      event.preventDefault();
      setShowLecturesMenu(!showLecturesMenu);
      setShowVideosMenu(false);
      setShowCommunityMenu(false);
    } else if (item.path === '/videos') {
      // If clicking Videos, show submenu
      event.preventDefault();
      setShowVideosMenu(!showVideosMenu);
      setShowLecturesMenu(false);
      setShowCommunityMenu(false);
    } else if (item.isServices && item.hasSubMenu) {
      // If clicking Services with Tax Docs enabled, show submenu
      event.preventDefault();
      setShowServicesMenu(!showServicesMenu);
      setShowLecturesMenu(false);
      setShowVideosMenu(false);
      setShowCommunityMenu(false);
    } else if (item.isCommunity && item.communityFeatures.length > 1) {
      // If clicking Community with multiple features, show submenu
      event.preventDefault();
      setShowCommunityMenu(!showCommunityMenu);
      setShowLecturesMenu(false);
      setShowVideosMenu(false);
      setShowServicesMenu(false);
    } else if (item.isCommunity && item.communityFeatures.length === 1) {
      // Single community feature - navigate directly
      navigate(item.path);
      setShowLecturesMenu(false);
      setShowVideosMenu(false);
      setShowCommunityMenu(false);
      setShowServicesMenu(false);
    } else {
      // Normal navigation
      setShowLecturesMenu(false);
      setShowVideosMenu(false);
      setShowCommunityMenu(false);
      setShowServicesMenu(false);
    }
  };

  // Submenu styles
  const submenuStyles = {
    container: {
      position: 'absolute',
      bottom: '100%',
      left: '0',
      backgroundColor: 'white',
      borderRadius: '8px 8px 0 0',
      boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
      transform: showLecturesMenu ? 'translateY(0)' : 'translateY(100%)',
      opacity: showLecturesMenu ? 1 : 0,
      transition: 'all 0.3s ease',
      pointerEvents: showLecturesMenu ? 'auto' : 'none',
      minWidth: '200px'
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #eee',
      textDecoration: 'none',
      color: '#333333',
      transition: 'background-color 0.2s ease'
    },
    icon: {
      marginRight: '12px',
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  // Hide on immersive routes that take over the full viewport — every
  // embedded-app route (/community/library, /community/housing, ...). Those
  // pages iframe an external webapp; the host nav (z-index 10001) was covering
  // the bottom of the iframe and double-stacking with the embedded app's own
  // navigation. The list comes from the registry so a new app is covered
  // automatically.
  const immersiveRoutes = EMBEDDED_APP_PATHS;
  if (immersiveRoutes.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return null;
  }

  // Debug logging
  console.log('[BottomNavigation] Final navItems:', navItems.map(item => item.label));
  console.log('[BottomNavigation] Rendering component', {
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    shouldShow,
    navItemsCount: navItems.length,
    themeColor,
    pathname: location.pathname,
    configVideoEnabled: config?.video_options_enabled
  });

  return (
    <nav
      id="bottom-navigation-debug"
      className="bottom-navigation page-theme"
      style={navigationStyle}
    >
      <div className="bottom-nav-container">
        {navItems.map((item, index) => (
          <div key={item.path} style={{ position: 'relative', flex: 1, display: 'flex' }}>
            {/* Submenu for Lectures */}
            {item.path === '/lectures' && (
              <div ref={menuRef} style={submenuStyles.container}>
                <Link
                  to="/lectures"
                  style={submenuStyles.item}
                  onClick={() => setShowLecturesMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                      <line x1="12" y1="6" x2="16" y2="6" />
                      <line x1="12" y1="10" x2="16" y2="10" />
                      <line x1="12" y1="14" x2="16" y2="14" />
                    </svg>
                  </span>
                  <span style={submenuStyles.label}>View Lectures</span>
                </Link>
                <Link
                  to="/lectures/search"
                  style={submenuStyles.item}
                  onClick={() => setShowLecturesMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  <span style={submenuStyles.label}>Search Lectures</span>
                </Link>
                {config?.publications_sheet_id && (
                  <Link
                    to="/publications"
                    style={{...submenuStyles.item, borderBottom: 'none'}}
                    onClick={() => setShowLecturesMenu(false)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span style={submenuStyles.icon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <path d="M14 2v6h6" />
                        <line x1="8" y1="10" x2="16" y2="10" />
                        <line x1="8" y1="14" x2="16" y2="14" />
                        <line x1="8" y1="18" x2="12" y2="18" />
                      </svg>
                    </span>
                    <span style={submenuStyles.label}>Publications</span>
                  </Link>
                )}
              </div>
            )}

            {/* Submenu for Videos */}
            {item.path === '/videos' && (
              <div ref={videosMenuRef} style={{
                ...submenuStyles.container,
                transform: showVideosMenu ? 'translateY(0)' : 'translateY(100%)',
                opacity: showVideosMenu ? 1 : 0,
                pointerEvents: showVideosMenu ? 'auto' : 'none'
              }}>
                <Link
                  to="/videos"
                  style={submenuStyles.item}
                  onClick={() => setShowVideosMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 14.65v-5.3L15 12l-5 2.65z" />
                      <path d="M18 4H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H6V6h12v12z" />
                    </svg>
                  </span>
                  <span style={{...submenuStyles.label, display: 'flex', alignItems: 'center', gap: '6px'}}>
                    Videos
                    {hasLiveVideos && (
                      <span style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: '#ff0000',
                        display: 'inline-block',
                        animation: 'livePulse 2s infinite',
                        boxShadow: '0 0 4px rgba(255,0,0,0.5)',
                      }} />
                    )}
                  </span>
                </Link>
                <Link
                  to="/shorts"
                  style={submenuStyles.item}
                  onClick={() => setShowVideosMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 14.65v-5.3L15 12l-5 2.65z" />
                      <path d="M18 4H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H6V6h12v12z" />
                      <rect x="7" y="2" width="10" height="2" rx="1" />
                      <rect x="7" y="20" width="10" height="2" rx="1" />
                    </svg>
                  </span>
                  <span style={submenuStyles.label}>YouTube Shorts</span>
                </Link>
                {config?.lecture_clips_enabled && (
                <Link
                  to="/lecture-clips"
                  style={{...submenuStyles.item, borderBottom: 'none'}}
                  onClick={() => setShowVideosMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z"/>
                    </svg>
                  </span>
                  <span style={submenuStyles.label}>Lecture Clips</span>
                </Link>
                )}
              </div>
            )}

            {/* Submenu for Community */}
            {item.isCommunity && item.communityFeatures.length > 1 && (
              <div ref={communityMenuRef} style={{
                ...submenuStyles.container,
                transform: showCommunityMenu ? 'translateY(0)' : 'translateY(100%)',
                opacity: showCommunityMenu ? 1 : 0,
                pointerEvents: showCommunityMenu ? 'auto' : 'none',
                left: 'calc(50% - 100px)', // Center menu on mobile
                minWidth: '200px' // Ensure text fits
              }}>
                {item.communityFeatures.map((feature, index) => (
                  <Link
                    key={feature.id}
                    to={feature.path}
                    style={{
                      ...submenuStyles.item,
                      borderBottom: index === item.communityFeatures.length - 1 ? 'none' : '1px solid #eee'
                    }}
                    onClick={() => setShowCommunityMenu(false)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span style={submenuStyles.icon}>
                      {feature.id === 'announcements' && '📢'}
                      {feature.id === 'calendar' && '📅'}
                      {feature.id === 'qa' && '❓'}
                    </span>
                    <span style={submenuStyles.label}>{feature.label}</span>
                  </Link>
                ))}
              </div>
            )}
            
            {/* Submenu for Services (when Tax Docs is enabled) */}
            {item.isServices && item.hasSubMenu && (
              <div ref={servicesMenuRef} style={{
                ...submenuStyles.container,
                transform: showServicesMenu ? 'translateY(0)' : 'translateY(100%)',
                opacity: showServicesMenu ? 1 : 0,
                pointerEvents: showServicesMenu ? 'auto' : 'none',
                left: 'auto',
                right: '0'
              }}>
                <Link
                  to="/community/services"
                  style={submenuStyles.item}
                  onClick={() => setShowServicesMenu(false)}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={submenuStyles.icon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 18c4.5-1.5 7-5.5 7-9V5l-6-2-6 2v4c0 3.5 2.5 7.5 7 9" />
                    </svg>
                  </span>
                  <span style={submenuStyles.label}>Services</span>
                </Link>
                {hasTaxDocs && (
                  <Link
                    to="/community/tax-docs"
                    style={{...submenuStyles.item, borderBottom: embeddedApps.length > 0 ? '1px solid #eee' : 'none'}}
                    onClick={() => setShowServicesMenu(false)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span style={submenuStyles.icon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <path d="M14 2v6h6" />
                        <line x1="9" y1="15" x2="15" y2="15" />
                      </svg>
                    </span>
                    <span style={submenuStyles.label}>Tax Docs</span>
                  </Link>
                )}
                {/* Embedded external apps — Library, Housing, ... Icons and
                    labels come from config/embeddedApps.js. */}
                {embeddedApps.map((app, index) => (
                  <Link
                    key={app.id}
                    to={app.path}
                    style={{
                      ...submenuStyles.item,
                      borderBottom: index === embeddedApps.length - 1 ? 'none' : '1px solid #eee'
                    }}
                    onClick={() => setShowServicesMenu(false)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span style={submenuStyles.icon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {app.iconPaths.map((d) => <path key={d} d={d} />)}
                      </svg>
                    </span>
                    <span style={submenuStyles.label}>{app.label}</span>
                  </Link>
                ))}
              </div>
            )}

            {/* Navigation button */}
            {item.path === '/' || item.path === '/lectures' || item.path === '/videos' || (item.isCommunity && item.communityFeatures.length > 1) || (item.isServices && item.hasSubMenu) ? (
              <button
                className={`bottom-nav-item ${isActive(item) ? 'active' : ''}`}
                aria-label={item.label}
                onClick={(e) => handleNavClick(e, item)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  font: 'inherit',
                  cursor: 'pointer',
                  padding: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                  pointerEvents: 'auto',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <span className="nav-icon" style={{ position: 'relative' }}>
                  {item.icon}
                  {item.path === '/videos' && hasLiveVideos && (
                    <span className="live-badge-indicator" />
                  )}
                </span>
                <span className="nav-label">{item.label}</span>
              </button>
            ) : (
              <Link
                to={item.path}
                className={`bottom-nav-item ${isActive(item) ? 'active' : ''}`}
                aria-label={item.label}
              >
                <span className="nav-icon" style={{ position: 'relative' }}>
                  {item.icon}
                  {item.path === '/videos' && hasLiveVideos && (
                    <span className="live-badge-indicator" />
                  )}
                </span>
                <span className="nav-label">{item.label}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default BottomNavigation;